import { useMemo, useState } from "react";
import { Select } from "@headlessui/react";
import { ref, remove, update } from "firebase/database";
import { useFirebase } from "../FbContex";
import { db } from "../firebase";
import { INode } from "../@types/device";
import { WithId } from "../@types/room";
import DeviceForm from "../components/DeviceForm";
import ConfirmDialog from "../components/ConfirmDialog";
import { allNodes, allRooms, roomName, swapOrder } from "../lib/selectors";
import {
  BTN_PRIMARY,
  SELECT_CLASS,
  TD,
  TD_HEAD,
  TH,
} from "../components/tableStyles";
import { AdminSection, OrderCell, ToggleCell } from "../components/table/Cells";

export default function Devices() {
  const { devices, rooms } = useFirebase();
  const [device, setDevice] = useState<INode | null>(null);
  const [target, setTarget] = useState<WithId<INode> | null>(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const all = useMemo(() => allNodes(devices), [devices]);
  const list = useMemo(
    () => (filter ? all.filter((n) => n.roomId === filter) : all),
    [all, filter],
  );

  /** A repeated (addr, stateKey, widget) is a genuine mistake, so flag it. */
  const dupes = useMemo(() => {
    const seen = new Map<string, number>();
    all.forEach((n) => {
      const k = `${n.addr}|${n.stateKey}|${n.widget}`;
      seen.set(k, (seen.get(k) ?? 0) + 1);
    });
    return seen;
  }, [all]);

  const isDupe = (n: INode) =>
    (dupes.get(`${n.addr}|${n.stateKey}|${n.widget}`) ?? 0) > 1;

  const toggle = (n: WithId<INode>) =>
    update(ref(db, `devices/${n.id}`), { disabled: !n.disabled });

  const confirmDelete = () => {
    if (!target) return;
    setBusy(true);
    remove(ref(db, `devices/${target.id}`))
      .catch((err) => alert(err.message))
      .finally(() => {
        setBusy(false);
        setTarget(null);
      });
  };

  return (
    <>
      <DeviceForm device={device} close={() => setDevice(null)} />

      <ConfirmDialog
        open={!!target}
        title={`Delete ${target?.title ?? "device"}?`}
        busy={busy}
        onConfirm={confirmDelete}
        onCancel={() => setTarget(null)}
      >
        <p>
          This removes the device configuration permanently. The hardware state
          at{" "}
          <code className="opacity-70">
            {target?.addr}/{target?.stateKey}
          </code>{" "}
          is not touched.
        </p>
      </ConfirmDialog>

      <AdminSection
        title="Devices"
        actions={
          <>
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className={`${SELECT_CLASS} w-40`}
            >
              <option value="">All rooms</option>
              {allRooms(rooms).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
            <button
              className={BTN_PRIMARY}
              type="button"
              onClick={() => setDevice({} as INode)}
            >
              Add new
            </button>
          </>
        }
      >
        <thead>
          <tr>
            <th className={TH}>title</th>
            <th className={TH}>room</th>
            <th className={TH}>state key</th>
            <th className={TH}>widget</th>
            <th className={TH}>enabled</th>
            <th className={TH}>order</th>
            <th className={TH}>action</th>
          </tr>
        </thead>
        <tbody>
          {list.length === 0 && (
            <tr>
              <td className={`${TD} opacity-50`} colSpan={7}>
                No devices yet.
              </td>
            </tr>
          )}
          {list.map((d, i) => {
            const rName = roomName(rooms, d.roomId);
            return (
              <tr key={d.id} className={d.disabled ? "opacity-50" : ""}>
                <th className={TD_HEAD}>
                  {d.icon && (
                    <i className={`fas fa-${d.icon} mr-2 opacity-60`} />
                  )}
                  {d.title}
                </th>
                <td className={TD}>
                  {rName ?? <span className="text-red-500">unknown</span>}
                </td>
                <td className={TD}>
                  <code className="opacity-70">
                    {d.addr}/{d.stateKey}
                  </code>
                  {isDupe(d) && (
                    <i
                      className="fas fa-triangle-exclamation text-amber-500 ml-2"
                      title="Another device uses the same address, state key and widget"
                    />
                  )}
                </td>
                <td className={TD}>{d.widget}</td>
                <ToggleCell checked={!d.disabled} onChange={() => toggle(d)} />
                <OrderCell
                  index={i}
                  count={list.length}
                  onMove={(dir) => swapOrder("devices", list, i, dir)}
                />
                <td className={TD}>
                  <i
                    onClick={() => setDevice({ ...d })}
                    className="fas fa-pencil text-emerald-500 mr-4 cursor-pointer hover:opacity-75"
                  />
                  <i
                    onClick={() => setTarget(d)}
                    className="fas fa-trash text-red-500 cursor-pointer hover:opacity-75"
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </AdminSection>
    </>
  );
}
